import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { useDataStore } from "@store/user_org";
import { Project } from "types";
import { User } from "types/user";

export interface SelectedSigner {
  id: string;
  signer_name: string;
  signer_email: string;
  signer_type: "conductor" | "client" | "partner" | "maitre_oeuvre";
  /** Optional initials painted before the date on the signed PDF. */
  initials?: string;
}

/** First letter of each word, uppercased, max 4 chars. */
function computeInitials(name: string | undefined | null): string {
  if (!name) return "";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase())
    .slice(0, 4)
    .join("");
}

export const SignerSelectionList = ({
  project,
  signers,
  setSigners,
}: {
  project: Project;
  signers: SelectedSigner[];
  setSigners: (signers: SelectedSigner[]) => void;
}) => {
  const orgMembers = useDataStore((state) => state.orgMembers);
  const userData = useDataStore((state) => state.userData);

  const currentUserEmail = userData?.infos?.email?.toLowerCase() || "";
  const currentUserHasSignature = !!userData?.infos?.signature_id;

  const clients = project.infos.client.contacts || [];
  const partners = project.infos.partners || [];

  const maitreOeuvrMembers: { label: string; member: User }[] = [];
  const commercialId = project.infos.team?.commercial_id;
  const conductorId = project.infos.team?.conductor_id;
  if (commercialId) {
    const m = orgMembers.find((u) => u.id === commercialId);
    if (m) maitreOeuvrMembers.push({ label: "Commercial", member: m });
  }
  if (conductorId) {
    const m = orgMembers.find((u) => u.id === conductorId);
    if (m) maitreOeuvrMembers.push({ label: "Conducteur de travaux", member: m });
  }

  const toggleSigner = (signer: SelectedSigner) => {
    const exists = signers.find((s) => s.id === signer.id);
    if (exists) setSigners(signers.filter((s) => s.id !== signer.id));
    else setSigners([
      ...signers,
      // Auto-derive initials when the signer is added. The conductor
      // edits them later from the field-placement modal sidebar — not
      // here — so this list stays focused on "who signs?".
      { ...signer, initials: signer.initials || computeInitials(signer.signer_name) },
    ]);
  };

  const hasAnyContact =
    maitreOeuvrMembers.some((m) => !!m.member.infos?.email) ||
    clients.some((c) => !!c.email) ||
    partners.some((p) => !!p.email);

  return (
    <div className="flex flex-col gap-4">
      {maitreOeuvrMembers.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-h3 font-medium text-neutral-700">Maître d'œuvre</div>
          {maitreOeuvrMembers.map(({ label, member }) => {
            const sigId = `maitre_oeuvre_${member.id}`;
            const email = member.infos?.email;
            if (!email) return null;
            const isCurrentUser = email.toLowerCase() === currentUserEmail;
            const willAutoSign = isCurrentUser && currentUserHasSignature;
            return (
              <SwitchBrick
                key={sigId}
                checked={!!signers.find((s) => s.id === sigId)}
                onChange={() =>
                  toggleSigner({
                    id: sigId,
                    signer_type: "maitre_oeuvre",
                    signer_name: member.infos?.display_name || member.infos?.full_name || "",
                    signer_email: email,
                  })
                }
                title={`${member.infos?.display_name || member.infos?.full_name || ""}`}
                description={
                  willAutoSign
                    ? `${label} — signature personnelle appliquée automatiquement`
                    : `${label} — ${email}`
                }
              />
            );
          })}
        </div>
      )}

      {clients.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-h3 font-medium text-neutral-700">Clients</div>
          {clients.map((contact) => {
            const sigId = `client_${contact.email || contact.last_name}`;
            if (!contact.email) return null;
            return (
              <SwitchBrick
                key={sigId}
                checked={!!signers.find((s) => s.id === sigId)}
                onChange={() =>
                  toggleSigner({
                    id: sigId,
                    signer_type: "client",
                    signer_name: `${contact.first_name} ${contact.last_name}`,
                    signer_email: contact.email!,
                  })
                }
                title={`${contact.first_name} ${contact.last_name}`}
                description={contact.email}
              />
            );
          })}
        </div>
      )}

      {partners.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-h3 font-medium text-neutral-700">Artisans</div>
          {partners
            .filter((p) => p.email)
            .map((partner) => {
              const sigId = `partner_${partner.id || partner.display_name}`;
              return (
                <SwitchBrick
                  key={sigId}
                  checked={!!signers.find((s) => s.id === sigId)}
                  onChange={() =>
                    toggleSigner({
                      id: sigId,
                      signer_type: "partner",
                      signer_name: partner.display_name || partner.company_name || "",
                      signer_email: partner.email || "",
                    })
                  }
                  title={partner.display_name || partner.company_name || ""}
                  description={partner.email || ""}
                />
              );
            })}
        </div>
      )}

      {!hasAnyContact && (
        <div className="text-h4 text-neutral-400">
          Aucun contact avec email associé à ce projet.
        </div>
      )}
    </div>
  );
};
